import React, {useEffect, useRef, useState} from 'react';
import Dialog from '../../../../components/dialogs/Dialog';
import DialogTitle from '../../../../components/dialogs/DialogTitle';
import DialogContent from '../../../../components/dialogs/DialogContent';
import TextField from '../../../../components/fields/textfield/TextField';
import Grid from '../../../../components/dialogs/Grid';
import {t} from '../../../../store/localization/TranslMessasge';
import {CoreDataTypeFormatter} from "./CoreDataTypeFormatter";
import {DataTypeTranslationKey} from './DataTypeTranslationKey';
import {CommonTranslation} from '../CommonTranslation';
import {DataTypeApiClient} from '../../../../common/apis/datatype/DataTypeApiClient';
import {
    CoreDataType,
    DataTypeDefinitionDto,
    DataTypeDefinitionUpdateDto
} from '../../../../common/apis/datatype/DataTypeDefinitionDto';
import {ButtonLayout, NegativeButtons, PositiveButtons} from "../../../../components/button/ButtonLayout";
import {CancelButton} from "../../../../components/button/CancelButton";
import DialogActions from "../../../../components/dialogs/DialogActions";
import Snackbar from "../snackbar/Snackbar";
import {ErrorTranslationKey} from "../ErrorTranslationKey";
import {SaveButton} from "../../../../components/button/SaveButton";

interface DataTypeUpdateDialogProps {
    open: boolean;
    dataType: DataTypeDefinitionDto;
    onUpdated: (dataTypeDefinitionId: string) => void;
    onClose: () => void;
}

export function DataTypeUpdateDialog({ open, dataType, onUpdated, onClose }: Readonly<DataTypeUpdateDialogProps>) {
    const dataTypeApiClient = useRef(new DataTypeApiClient());

    const [dataTypeUpdate, setDataTypeUpdate] = useState<Partial<DataTypeDefinitionUpdateDto>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [inProgress, setInProgress] = useState(false);

    useEffect(() => {
        if (open) {
            setDataTypeUpdate({
                name: dataType.name,
                description: dataType.description
            });
            setErrors({});
        }
    }, [open, dataType]);

    function updateDataType() {
        if (!validateForm()) {
            return;
        }

        setInProgress(true);

        const dataTypeDefinitionUpdate: DataTypeDefinitionUpdateDto = {
            name: dataTypeUpdate.name!.trim(),
            description: dataTypeUpdate.description?.trim()
        };

        dataTypeApiClient.current.update(dataType.id, dataTypeDefinitionUpdate)
            .then(() => {
                onUpdated(dataType.id);
                onClose();
                Snackbar.success(t(CommonTranslation.DATA_SAVED_SUCESSFULLY));
            })
            .catch((error) => Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), error))
            .finally(() => setInProgress(false));
    }

    function validateForm(): boolean {
        const newErrors: Record<string, string> = {};
        if (!dataTypeUpdate.name?.trim()) {
            newErrors.name = t(CommonTranslation.FILL_OUT_THIS_FIELD);
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    let coreDataTypes = Object.values(CoreDataType);
    coreDataTypes.sort();

    return (
            <Dialog open={open} onClose={onClose} maxWidth="md">
                <DialogTitle title={t(DataTypeTranslationKey.EDIT_DATA_TYPE)}
                        onDialogClosed={onClose} />
                <DialogContent>
                    <Grid container>
                        <Grid item xs={12} sm={7}>
                            <TextField
                                    label={t(DataTypeTranslationKey.NAME)}
                                    value={dataTypeUpdate.name}
                                    onChange={value => setDataTypeUpdate({...dataTypeUpdate, name: value})}
                                    errorMessage={errors.name}
                                    required={true}/>
                        </Grid>
                        <Grid item xs={12} sm={5}>
                            <TextField id={"core-data-type"}
                                    label={t(DataTypeTranslationKey.CORE_DATA_TYPE)}
                                    value={CoreDataTypeFormatter.formatCoreDataType(dataType.coreDataType, t)}
                                    readOnly={true}/>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                    label={t(CommonTranslation.DESCRIPTION)}
                                    value={dataTypeUpdate.description ?? ''}
                                    onChange={value => setDataTypeUpdate({...dataTypeUpdate, description: value})}
                                    multiline
                                    rows={4}/>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <ButtonLayout>
                        <PositiveButtons>
                            <SaveButton onClick={updateDataType} disabled={inProgress} />
                        </PositiveButtons>
                        <NegativeButtons>
                            <CancelButton onClick={onClose} disabled={inProgress} />
                        </NegativeButtons>
                    </ButtonLayout>
                </DialogActions>
            </Dialog>
    );
}
